import React, { useMemo } from 'react';
import { LineChart, LineSeriesType } from '@mui/x-charts';
import { Box } from '@mui/material';
import moment from 'moment';

interface ChartItem {
  month: string;
  usersUsingCount: number;
  messagesCount: number;
  eventsCount: number;
}

const DashboardChart = ({ chartData }: { chartData: ChartItem[] }) => {
  const data = useMemo(() => {
    const sorted = [...chartData].sort(
      (a, b) =>
        new Date(`${a.month}-01`).getTime() -
        new Date(`${b.month}-01`).getTime()
    );
    return sorted.map((item) => ({
      month: moment(new Date(`${item.month}-01`)).format('MMM YYYY'),
      users: item.usersUsingCount,
      messages: item.messagesCount,
      events: item.eventsCount,
    }));
  }, [chartData]);

  // Dynamically calculate min and max for y-axis
  const allValues = useMemo(
    () => [
      ...data.map((item) => item.users),
      ...data.map((item) => item.messages),
      ...data.map((item) => item.events),
    ],
    [data]
  );
  const yMin = Math.min(...allValues);
  const yMax = Math.max(...allValues);
  const yAxisRange = yMax - yMin > 0 ? yMax - yMin : 10; // Ensure range is at least 10 if all values are same

  const series = useMemo(
    () =>
      [
        {
          type: 'line',
          curve: 'linear',
          data: data.map((item) => item.users),
          label: 'Users',
          color: '#D90D07',
        },
        {
          type: 'line',
          curve: 'linear',
          data: data.map((item) => item.messages),
          label: 'Messages',
          color: '#CDA767',
        },
        {
          type: 'line',
          curve: 'linear',
          data: data.map((item) => item.events),
          label: 'Events',
          color: '#254BD1',
        },
      ] as LineSeriesType[],
    [data]
  );

  return (
    <Box sx={{ width: '100%', height: '100%', minHeight: 300 }}>
      <LineChart
        series={series}
        xAxis={[
          {
            scaleType: 'point',
            data: data.map((item) => item.month),
            disableLine: true,
          },
        ]}
        yAxis={[{ min: yMin, max: yMax + yAxisRange * 0.1, disableLine: true }]}
        margin={{ top: 20, bottom: 70 }}
        height={300}
        slotProps={{
          legend: {
            position: { vertical: 'bottom', horizontal: 'middle' },
            labelStyle: { fontSize: '12px' },
          },
        }}
        grid={{ horizontal: true }}
      />
    </Box>
  );
};

export default DashboardChart;
